// src/pages/Platform/Chatbot/ChatbotBuilder/Customize/Theme/StyleSettings.js

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Check, Trash2, Save, ChevronDown, ChevronUp } from 'lucide-react';
import logger from '../../../../../../utils/logger';

const MAX_CUSTOM_THEMES = 3;
const CUSTOM_THEMES_KEY = 'chatbotCustomThemes';

function StyleSettings({
  isEditMode,
  headerColor,
  setHeaderColor,
  chatBackgroundColor,
  setChatBackgroundColor,
  botMessageBoxColor,
  setBotMessageBoxColor,
  botMessageTextColor,
  setBotMessageTextColor,
  userMessageBoxColor,
  setUserMessageBoxColor,
  userMessageTextColor,
  setUserMessageTextColor,
  updateConfig,
  headerStyle = 'modern',
  setHeaderStyle,
  saveConfig,
}) {
  const [showAllThemes, setShowAllThemes] = useState(false);

  const colorOptions = useMemo(
    () => [
      { key: 'headerColor', name: 'Header Background', color: headerColor, setter: setHeaderColor },
      { key: 'chatBackgroundColor', name: 'Chat Background', color: chatBackgroundColor, setter: setChatBackgroundColor },
      { key: 'botMessageBoxColor', name: 'AI Bubble', color: botMessageBoxColor, setter: setBotMessageBoxColor },
      { key: 'botMessageTextColor', name: 'AI Text', color: botMessageTextColor, setter: setBotMessageTextColor },
      { key: 'userMessageBoxColor', name: 'User Bubble', color: userMessageBoxColor, setter: setUserMessageBoxColor },
      { key: 'userMessageTextColor', name: 'User Text', color: userMessageTextColor, setter: setUserMessageTextColor },
    ],
    [
      headerColor,
      chatBackgroundColor,
      botMessageBoxColor,
      botMessageTextColor,
      userMessageBoxColor,
      userMessageTextColor,
      setHeaderColor,
      setChatBackgroundColor,
      setBotMessageBoxColor,
      setBotMessageTextColor,
      setUserMessageBoxColor,
      setUserMessageTextColor,
    ]
  );

  const themes = useMemo(
    () => [
      {
        name: 'Monochrome',
        header: '#292929',
        chatBackground: '#FFFFFF',
        botMessageBox: '#F7F7F7',
        botMessageText: '#292929',
        userMessageBox: '#292929',
        userMessageText: '#F7F7F7',
      },
      {
        name: 'Modern Minimalist',
        header: '#454545',
        chatBackground: '#ecf0f1',
        botMessageBox: '#bdc3c7',
        botMessageText: '#2c3e50',
        userMessageBox: '#454545',
        userMessageText: '#ffffff',
      },
      {
        name: 'Autumn Harvest',
        header: '#8D6E63',
        chatBackground: '#EFEBE9',
        botMessageBox: '#D7CCC8',
        botMessageText: '#4E342E',
        userMessageBox: '#A1887F',
        userMessageText: '#FFFFFF',
      },
      {
        name: 'Corporate Elegance',
        header: '#003366',
        chatBackground: '#f2f2f2',
        botMessageBox: '#e6f0ff',
        botMessageText: '#003366',
        userMessageBox: '#879AC4',
        userMessageText: '#FFFFFF',
      },
      {
        name: 'Purple Dreams',
        header: '#6C63FF',
        chatBackground: '#F5F7FF',
        botMessageBox: '#D1D4FF',
        botMessageText: '#3F3D56',
        userMessageBox: '#6C63FF',
        userMessageText: '#FFFFFF',
      },
      {
        name: 'Dark Mode',
        header: '#2c3e50',
        chatBackground: '#1e272e',
        botMessageBox: '#576574',
        botMessageText: '#d2dae2',
        userMessageBox: '#3E4651',
        userMessageText: '#ffffff',
      },
      {
        name: 'Forest Canopy',
        header: '#435740',
        chatBackground: '#E8F5E9',
        botMessageBox: '#A5D6A7',
        botMessageText: '#1B5E20',
        userMessageBox: '#66BB6A',
        userMessageText: '#FFFFFF',
      },
      {
        name: 'Executive Dark',
        header: '#212729',
        chatBackground: '#212729',
        botMessageBox: '#2F3841',
        botMessageText: '#999999',
        userMessageBox: '#424242',
        userMessageText: '#999999',
      },
      {
        name: 'WhatsApp Classic',
        header: '#075E54',
        chatBackground: '#FFFFFF',
        botMessageBox: '#DCF8C6',
        botMessageText: '#1C1C1C',
        userMessageBox: '#075E54',
        userMessageText: '#FFFFFF',
      },
      {
        name: 'Mint Freshness',
        header: '#26A69A',
        chatBackground: '#EBFFFA',
        botMessageBox: '#A7FFEB',
        botMessageText: '#00695C',
        userMessageBox: '#00ACC1',
        userMessageText: '#FFFFFF',
      },
    ],
    []
  );

  // Custom saved themes state
  const [savedCustomThemes, setSavedCustomThemes] = useState([]);

  // Get chatbot ID for storing themes per chatbot
  const chatbotId = localStorage.getItem('chatbotId') || 'default';

  // Load saved custom themes from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(`${CUSTOM_THEMES_KEY}_${chatbotId}`);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed)) {
          setSavedCustomThemes(parsed.slice(0, MAX_CUSTOM_THEMES));
        }
      }
    } catch (error) {
      logger.error('Error loading custom themes:', error);
    }
  }, [chatbotId]);

  // Save current theme as a custom theme
  const saveCurrentTheme = useCallback(() => {
    const currentTheme = {
      name: `My Theme ${savedCustomThemes.length + 1}`,
      savedAt: new Date().toISOString(),
      header: headerColor,
      chatBackground: chatBackgroundColor,
      botMessageBox: botMessageBoxColor,
      botMessageText: botMessageTextColor,
      userMessageBox: userMessageBoxColor,
      userMessageText: userMessageTextColor,
    };

    // Add to beginning, limit to MAX_CUSTOM_THEMES
    const updatedThemes = [currentTheme, ...savedCustomThemes].slice(0, MAX_CUSTOM_THEMES);

    setSavedCustomThemes(updatedThemes);
    localStorage.setItem(`${CUSTOM_THEMES_KEY}_${chatbotId}`, JSON.stringify(updatedThemes));

    logger.log('Custom theme saved:', currentTheme.name);
  }, [
    savedCustomThemes,
    chatbotId,
    headerColor,
    chatBackgroundColor,
    botMessageBoxColor,
    botMessageTextColor,
    userMessageBoxColor,
    userMessageTextColor,
  ]);

  // Delete a custom theme
  const deleteCustomTheme = useCallback((index) => {
    const updatedThemes = savedCustomThemes.filter((_, i) => i !== index);
    setSavedCustomThemes(updatedThemes);
    localStorage.setItem(`${CUSTOM_THEMES_KEY}_${chatbotId}`, JSON.stringify(updatedThemes));
    logger.log('Custom theme deleted at index:', index);
  }, [savedCustomThemes, chatbotId]);

  const persistConfig = useCallback((updatedConfig) => {
    const savedConfig = JSON.parse(localStorage.getItem('chatbotConfig')) || {};
    localStorage.setItem('chatbotConfig', JSON.stringify({ ...savedConfig, ...updatedConfig }));
  }, []);

  const applyTheme = useCallback(
    (theme) => {
      setHeaderColor(theme.header);
      setChatBackgroundColor(theme.chatBackground);
      setBotMessageBoxColor(theme.botMessageBox);
      setBotMessageTextColor(theme.botMessageText);
      setUserMessageBoxColor(theme.userMessageBox);
      setUserMessageTextColor(theme.userMessageText);

      const updatedConfig = {
        headerColor: theme.header,
        chatBackgroundColor: theme.chatBackground,
        botMessageBoxColor: theme.botMessageBox,
        botMessageTextColor: theme.botMessageText,
        userMessageBoxColor: theme.userMessageBox,
        userMessageTextColor: theme.userMessageText,
      };

      persistConfig(updatedConfig);
      updateConfig(updatedConfig);

      // Save to server immediately to ensure persistence
      if (saveConfig) {
        saveConfig(updatedConfig).catch(err =>
          logger.error('Error saving theme config:', err)
        );
      }
    },
    [
      setHeaderColor,
      setChatBackgroundColor,
      setBotMessageBoxColor,
      setBotMessageTextColor,
      setUserMessageBoxColor,
      setUserMessageTextColor,
      updateConfig,
      persistConfig,
      saveConfig,
    ]
  );

  const handleColorChange = useCallback((key, value) => {
    // Immediate state update performed by the specific setter passed in params or context
    // Here we need to find the setter from colorOptions or just use the logic below

    // We update generic state via updateConfig
    const updatePayload = { [key]: value };
    updateConfig(updatePayload);

    // Call specific setter if needed for local state reflected in UI instantly
    switch (key) {
      case 'headerColor': setHeaderColor(value); break;
      case 'chatBackgroundColor': setChatBackgroundColor(value); break;
      case 'botMessageBoxColor': setBotMessageBoxColor(value); break;
      case 'botMessageTextColor': setBotMessageTextColor(value); break;
      case 'userMessageBoxColor': setUserMessageBoxColor(value); break;
      case 'userMessageTextColor': setUserMessageTextColor(value); break;
      default: break;
    }
  }, [
    updateConfig,
    setHeaderColor, setChatBackgroundColor, setBotMessageBoxColor,
    setBotMessageTextColor, setUserMessageBoxColor, setUserMessageTextColor
  ]);


  const handleHeaderStyleChange = useCallback((style) => {
    setHeaderStyle(style);
    updateConfig({ headerStyle: style });
    const savedConfig = JSON.parse(localStorage.getItem('chatbotConfig')) || {};
    localStorage.setItem('chatbotConfig', JSON.stringify({ ...savedConfig, headerStyle: style }));
  }, [setHeaderStyle, updateConfig]);

  // Load saved config on mount if not edit mode (just to be safe, though parent usually handles this)
  useEffect(() => {
    if (!isEditMode) {
      const savedConfig = JSON.parse(localStorage.getItem('chatbotConfig')) || {};
      if (savedConfig.headerColor) setHeaderColor(savedConfig.headerColor);
      if (savedConfig.chatBackgroundColor) setChatBackgroundColor(savedConfig.chatBackgroundColor);
      if (savedConfig.botMessageBoxColor) setBotMessageBoxColor(savedConfig.botMessageBoxColor);
      if (savedConfig.botMessageTextColor) setBotMessageTextColor(savedConfig.botMessageTextColor);
      if (savedConfig.userMessageBoxColor) setUserMessageBoxColor(savedConfig.userMessageBoxColor);
      if (savedConfig.userMessageTextColor) setUserMessageTextColor(savedConfig.userMessageTextColor);
      if (savedConfig.headerStyle) setHeaderStyle(savedConfig.headerStyle);
    }
  }, [
    isEditMode,
    setHeaderColor,
    setChatBackgroundColor,
    setBotMessageBoxColor,
    setBotMessageTextColor,
    setUserMessageBoxColor,
    setUserMessageTextColor,
    setHeaderStyle,
  ]);

  const visibleThemes = showAllThemes ? themes : themes.slice(0, 4);

  return (
    <div className="flex h-full w-full bg-slate-50/50 p-3 gap-3 overflow-hidden">
      <div className="w-full h-full flex flex-col bg-white rounded-2xl border border-slate-200/60 shadow-sm overflow-hidden">
        {/* Component Header */}
        <div className="p-4 border-b border-slate-100 bg-white sticky top-0 z-10">
          <h2 className="text-lg font-bold tracking-tight text-slate-900">Theme & Appearance</h2>
          <p className="text-xs text-slate-500 font-medium mt-0.5">Customize your chatbot's visual identity</p>
        </div>

        <div className="flex-1 overflow-y-auto custom-scrollbar">
          <div className="max-w-3xl mx-auto px-8 py-8 flex flex-col gap-10">

            {/* 1. Header Style (Top Priority) */}
            <section>
              <div className="border-b border-slate-100 pb-2 mb-6">
                <label className="block text-sm font-semibold text-slate-900 pb-1">Header Style</label>
                <p className="text-xs text-slate-500">Choose between a compact minimal header or a modern gradient header design.</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <label className={`cursor-pointer group relative block rounded-xl border transition-all overflow-hidden ${headerStyle === 'compact' ? 'border-blue-600' : 'border-slate-200 hover:border-slate-300'}`}>
                  <input
                    type="radio"
                    name="headerStyle"
                    value="compact"
                    checked={headerStyle === 'compact'}
                    onChange={(e) => handleHeaderStyleChange(e.target.value)}
                    className="sr-only"
                  />
                  <div className="bg-slate-50 h-28 relative flex flex-col overflow-hidden">
                    <div className="w-full h-10 shrink-0" style={{ backgroundColor: headerColor !== '#ffffff' ? headerColor : '#1e293b' }}>
                      <div className="w-full h-full opacity-20 bg-white/10"></div>
                    </div>
                    <div className="flex-1 p-3">
                      <div className="w-3/4 h-2 bg-slate-200 rounded-full"></div>
                      <div className="w-1/2 h-2 bg-slate-200 rounded-full mt-2"></div>
                    </div>
                  </div>
                  <div className="px-3 py-2 bg-white border-t border-slate-100 flex items-center justify-between">
                    <span className={`text-xs font-semibold ${headerStyle === 'compact' ? 'text-blue-700' : 'text-slate-600'}`}>Compact</span>
                    {headerStyle === 'compact' && <Check size={14} className="text-blue-600" />}
                  </div>
                </label>

                <label className={`cursor-pointer group relative block rounded-xl border transition-all overflow-hidden ${headerStyle === 'modern' ? 'border-blue-600' : 'border-slate-200 hover:border-slate-300'}`}>
                  <input
                    type="radio"
                    name="headerStyle"
                    value="modern"
                    checked={headerStyle === 'modern'}
                    onChange={(e) => handleHeaderStyleChange(e.target.value)}
                    className="sr-only"
                  />
                  <div className="bg-slate-50 h-28 relative flex flex-col overflow-hidden">
                    <div className="w-full h-16 shrink-0"
                      style={{ background: `linear-gradient(180deg, ${headerColor} 0%, ${headerColor}40 100%)` }}
                    ></div>
                    <div className="flex-1 p-3 -mt-6 relative z-10">
                      <div className="w-full h-full bg-white rounded-t-lg border-t border-l border-r border-slate-100 p-2">
                        <div className="w-3/4 h-2 bg-slate-200 rounded-full"></div>
                      </div>
                    </div>
                  </div>
                  <div className="px-3 py-2 bg-white border-t border-slate-100 flex items-center justify-between">
                    <span className={`text-xs font-semibold ${headerStyle === 'modern' ? 'text-blue-700' : 'text-slate-600'}`}>Modern</span>
                    {headerStyle === 'modern' && <Check size={14} className="text-blue-600" />}
                  </div>
                </label>
              </div>
            </section>

            {/* 2. Quick Presets (Refined Skeleton) */}
            <section>
              <div className="border-b border-slate-100 pb-2 mb-6">
                <label className="block text-sm font-semibold text-slate-900 pb-1">Quick Presets</label>
                <p className="text-xs text-slate-500">Quickly apply pre-designed color themes to give your chatbot a professional look.</p>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4">
                {visibleThemes.map((theme, index) => (
                  <button
                    key={index}
                    onClick={() => applyTheme(theme)}
                    className="group relative flex flex-col gap-2 text-left rounded-xl transition-all duration-200 focus:outline-none"
                  >
                    {/* Refined Skeleton UI */}
                    <div className="w-full h-24 rounded-lg pointer-events-none select-none border border-slate-100 overflow-hidden relative flex flex-col" style={{ backgroundColor: theme.chatBackground }}>

                      {/* Header */}
                      <div className="w-full h-5 flex items-center px-2 shrink-0" style={{ backgroundColor: theme.header }}>
                        <div className="w-6 h-1.5 rounded-full bg-white/20"></div>
                      </div>

                      {/* Chat Area */}
                      <div className="flex-1 p-2 flex flex-col gap-2 justify-center">
                        {/* Bot Message */}
                        <div className="flex w-full justify-start">
                          <div className="max-w-[80%] h-6 rounded-lg rounded-tl-sm flex items-center px-2" style={{ backgroundColor: theme.botMessageBox }}>
                            <div className="w-8 h-1 rounded-full opacity-60" style={{ backgroundColor: theme.botMessageText }}></div>
                          </div>
                        </div>

                        {/* User Message */}
                        <div className="flex w-full justify-end">
                          <div className="max-w-[80%] h-6 rounded-lg rounded-tr-sm flex items-center px-2 justify-end" style={{ backgroundColor: theme.userMessageBox }}>
                            <div className="w-8 h-1 rounded-full opacity-60" style={{ backgroundColor: theme.userMessageText }}></div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="px-1">
                      <span className="block text-xs font-semibold text-slate-900 group-hover:text-blue-600 transition-colors">{theme.name}</span>
                    </div>
                  </button>
                ))}
              </div>

              {/* Show More / Show Less Shutter */}
              <div className="mt-4 flex justify-center">
                <button
                  onClick={() => setShowAllThemes(!showAllThemes)}
                  className="flex items-center gap-2 px-4 py-2 text-xs font-bold text-slate-500 hover:text-slate-800 bg-slate-50 hover:bg-slate-100 rounded-full transition-all"
                >
                  {showAllThemes ? (
                    <>Show Less <ChevronUp size={14} /></>
                  ) : (
                    <>Show All Presets ({themes.length - 4} more) <ChevronDown size={14} /></>
                  )}
                </button>
              </div>
            </section>

            {/* 3. Detailed Colors */}
            <section>
              <div className="border-b border-slate-100 pb-2 mb-6">
                <label className="block text-sm font-semibold text-slate-900 pb-1">Color Palette</label>
                <p className="text-xs text-slate-500">Customize individual components of the chatbot to match your brand's style.</p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {colorOptions.map((option) => (
                  <div key={option.key} className="flex items-center justify-between p-3 border border-slate-200 rounded-xl bg-slate-50/50 hover:bg-white hover:border-slate-300 hover:shadow-sm transition-all">
                    <span className="text-sm font-medium text-slate-700">{option.name}</span>
                    <div className="flex items-center gap-3">
                      <div className="text-xs font-mono text-slate-400 uppercase hidden sm:block">
                        {option.color}
                      </div>
                      <div className="relative w-8 h-8 rounded-full overflow-hidden ring-1 ring-slate-200 cursor-pointer hover:scale-110 transition-transform">
                        <input
                          type="color"
                          value={option.color}
                          onChange={(e) => handleColorChange(option.key, e.target.value)}
                          className="absolute inset-0 w-[150%] h-[150%] -top-1/4 -left-1/4 p-0 border-0 cursor-pointer"
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* 4. User Saved Themes with Save Button */}
            <section className="pt-4 border-t border-slate-100">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <label className="block text-sm font-semibold text-slate-900">Saved Custom Themes</label>
                  <p className="text-xs text-slate-500 mt-1">Save your current theme to restore it later. You can save up to {MAX_CUSTOM_THEMES} custom themes.</p>
                </div>
                <button
                  onClick={saveCurrentTheme}
                  disabled={savedCustomThemes.length >= MAX_CUSTOM_THEMES}
                  className="flex items-center gap-2 px-3 py-1.5 text-xs font-semibold text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Save size={14} />
                  Save Current
                </button>
              </div>

              {savedCustomThemes.length > 0 ? (
                <div className="space-y-3">
                  {savedCustomThemes.map((theme, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-white border border-slate-200 rounded-xl hover:shadow-sm transition-all group">
                      <div className="flex items-center gap-3 cursor-pointer flex-1" onClick={() => applyTheme(theme)}>
                        <div className="flex -space-x-1">
                          <div className="w-6 h-6 rounded-full ring-2 ring-white" style={{ backgroundColor: theme.header }}></div>
                          <div className="w-6 h-6 rounded-full ring-2 ring-white" style={{ backgroundColor: theme.chatBackground }}></div>
                          <div className="w-6 h-6 rounded-full ring-2 ring-white" style={{ backgroundColor: theme.botMessageBox }}></div>
                        </div>
                        <span className="text-sm font-medium text-slate-700 group-hover:text-blue-600 transition-colors">{theme.name}</span>
                      </div>
                      <button
                        onClick={() => deleteCustomTheme(index)}
                        className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="p-6 text-center bg-slate-50 rounded-xl border border-dashed border-slate-200">
                  <p className="text-xs text-slate-500">
                    Customize your theme above and click "Save Current" to create your own preset.
                  </p>
                </div>
              )}
            </section>

          </div>
        </div>
      </div>
    </div>
  );
}

StyleSettings.propTypes = {
  headerColor: PropTypes.string.isRequired,
  setHeaderColor: PropTypes.func.isRequired,
  chatBackgroundColor: PropTypes.string.isRequired,
  setChatBackgroundColor: PropTypes.func.isRequired,
  botMessageBoxColor: PropTypes.string.isRequired,
  setBotMessageBoxColor: PropTypes.func.isRequired,
  botMessageTextColor: PropTypes.string.isRequired,
  setBotMessageTextColor: PropTypes.func.isRequired,
  userMessageBoxColor: PropTypes.string.isRequired,
  setUserMessageBoxColor: PropTypes.func.isRequired,
  userMessageTextColor: PropTypes.string.isRequired,
  setUserMessageTextColor: PropTypes.func.isRequired,
  updateConfig: PropTypes.func.isRequired,
  headerStyle: PropTypes.oneOf(['compact', 'modern']),
  setHeaderStyle: PropTypes.func,
  saveConfig: PropTypes.func,
  isEditMode: PropTypes.bool
};

export default StyleSettings;
